import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { CaseClinicalSpecialistResource, CaseOCSConsoleResource, CaseResource } from '@/api/apis/case';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { employeeQueries } from '@/queries/employee';
import { useInfiniteOcsConsoleListQuery } from '@/queries/ocs-consoles';

type UseClinicalResourceTrialProgramsParams = {
  clinicalSpecialists: CaseClinicalSpecialistResource[];
  ocsConsoles: CaseResource<CaseOCSConsoleResource>[];
};

type UseClinicalResourceTrialProgramsResult = {
  clinicalSpecialistTrialProgramsById: Record<string, TrialProgram[]>;
  ocsConsoleTrialProgramsById: Record<string, TrialProgram[]>;
};

export const useClinicalResourceTrialPrograms = ({
  clinicalSpecialists,
  ocsConsoles,
}: UseClinicalResourceTrialProgramsParams): UseClinicalResourceTrialProgramsResult => {
  const clinicalSpecialistIds = useMemo(
    () => Array.from(new Set(clinicalSpecialists.map(({ resourceId }) => resourceId).filter(Boolean))),
    [clinicalSpecialists],
  );

  const ocsConsoleIds = useMemo(
    () => Array.from(new Set(ocsConsoles.map(({ resourceId }) => resourceId).filter(Boolean))),
    [ocsConsoles],
  );

  const { data: clinicalSpecialistsData } = useQuery({
    ...employeeQueries.listByIds(clinicalSpecialistIds),
    enabled: clinicalSpecialistIds.length > 0,
  });

  const { data: ocsConsolesData } = useInfiniteOcsConsoleListQuery(
    { ids: ocsConsoleIds.join(',') },
    !!ocsConsoleIds.length,
  );

  const clinicalSpecialistTrialProgramsById = useMemo(() => {
    const items = clinicalSpecialistsData?.items ?? [];

    return items.reduce<Record<string, TrialProgram[]>>((acc, employee) => {
      acc[employee.id] = employee.trialPrograms ?? [];
      return acc;
    }, {});
  }, [clinicalSpecialistsData]);

  const ocsConsoleTrialProgramsById = useMemo(() => {
    const items = ocsConsolesData?.pages.flatMap((page) => page.items) ?? [];

    return items.reduce<Record<string, TrialProgram[]>>((acc, console) => {
      acc[console.id] = console.trialProgram ? [console.trialProgram] : [];
      return acc;
    }, {});
  }, [ocsConsolesData]);

  return {
    clinicalSpecialistTrialProgramsById,
    ocsConsoleTrialProgramsById,
  };
};
